<template>
  <div>
    <t-section-header header="Informações" icon="mdi-information-outline" />
    <v-footer color="footer-background" class="pt-4">
      <t-container>
        <v-row justify="space-between">
          <v-col
            v-for="item in items"
            :key="item.title"
            cols="12"
            sm="6"
            lg="auto"
            class="d-flex flex-column text-body"
            :class="{ 'align-center': mobile }"
          >
            <span
              cols="auto"
              class="footer-title-text font-weight-medium text-wrap mb-2 text-uppercase"
              style="overflow-wrap: break-word;"
            >
              {{ item.title }}
            </span>

            <template v-for="link in item.links" :key="link.to">
              <router-link
                :to="link.to"
                class="footer-item-text text-decoration-none text-body"
                style="border-bottom: 1px solid; padding: 0.1rem; margin-bottom: 0.4rem"
              >
                {{ link.label }}
              </router-link>
            </template>
          </v-col>

          <v-col
            cols="12"
            sm="6"
            lg="4"
            class="d-flex align-center flex-column my-3 py-0 px-0"
          >
            <t-logo-footer :style="{'width': mobile ? '160px' : '200px'}" class="mt-2" />
            <v-row>
              <v-col
                :cols="mobile ? '12' : 'auto'"
                :class="{'d-flex': true, 'flex-column': true, 'align-center': true, 'justify-center': true, 'mt-5': mobile, 'mt-0': !mobile }"
              >
                <small v-for="line in footerLines" :key="line" style="color: unset; font-size: 11px;">
                  {{ line }}
                </small>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <!-- Informações Mobile -->
        <template v-if="mobile">
          <v-row>
            <v-col cols="12" class="text-center pb-0">
              <small style="font-size: 11px"> Ingresso de Vantagens Ltda | CNPJ: 51.781.190/0001-62 </small>
            </v-col>
            <v-col cols="12" class="text-center pb-0">
              <small style="font-size: 11px"> contato@ingressodevantagens.com.br </small>
            </v-col>
          </v-row>
          <v-row justify="center" class="py-6 text-caption text-center" no-gutters>
            <v-col cols="auto">
              Copyright © {{ year }} {{ producer.title }} by Grupo Dataclick
            </v-col>

            <v-col cols="12">
              v{{ app.version }} ({{ app.commit }})
            </v-col>
          </v-row>
        </template>
        <template v-else>
          <v-row>
            <v-col cols="12" class="text-center pb-0">
              <small>
                Ingresso de Vantagens Ltda | CNPJ: 51.781.190/0001-62
              </small>
            </v-col>
            <v-col cols="12" class="text-center pb-0">
              <small>
                contato@ingressodevantagens.com.br
              </small>
            </v-col>
          </v-row>
          <v-row justify="center" class="py-6 text-caption text-center" no-gutters>
            <v-col cols="auto">
              Copyright © {{ year }} {{ producer.title }} ( v{{ app.version }} -
              {{ app.commit }}) by Grupo Dataclick
            </v-col>
          </v-row>
        </template>
        <!-- Informações Desktop -->
      </t-container>
    </v-footer>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { DateTime } from "luxon";
import { useDisplay } from "vuetify";
import TLogoFooter from "./TLogoFooter.vue";
import TSectionHeader from "./TSectionHeader.vue";
import type { WhitelabelPage, WhitelabelPageKey, WhitelabelProducer } from "@/types/Producer";
import { useAppStore } from "@/stores/app";
import { useProducersStore } from "@/stores/producers";

defineOptions({
  name: "TheFooter",
});

const { mobile } = useDisplay();
const app = useAppStore();
const producersStore = useProducersStore();

interface FooterLink {
  label: string;
  to: string;
  page?: WhitelabelPageKey;
}

interface FooterGroup {
  title: string;
  links: FooterLink[];
}

const groups: FooterGroup[] = [
  {
    title: "Institucional",
    links: [
      {
        label: "Quem Somos",
        to: "/sobre",
        page: "aboutUs",
      },
      {
        label: "Termos de Uso",
        to: "/termos",
        page: "termsOfService",
      },
    ],
  },

  {
    title: "Nossas Políticas",
    links: [
      {
        label: "De Privacidade",
        to: "/privacidade",
        page: "privacyPolicy",
      },
      {
        label: "De Cancelamento",
        to: "/cancelamentos",
        page: "cancelationPolicy",
      },
      {
        label: "De Meia-entrada",
        to: "/meia-entrada",
        page: "halfPricePolicy",
      },
    ],
  },

  {
    title: "Contato",
    links: [
      {
        label: "Fale Conosco",
        to: "/suporte",
      },
      {
        label: "Dúvidas Frequentes",
        to: "/faq",
        page: "faq",
      },
    ],
  },
];

function pageConfig(link: FooterLink): WhitelabelPage | undefined {
  return link.page ? producer.value.pages?.[link.page] : undefined;
}

// Applies the producer page overrides, hiding the pages it disabled and renaming the ones it customized
const items = computed<FooterGroup[]>(() => groups
  .map(group => ({
    ...group,
    links: group.links
      .filter(link => pageConfig(link)?.show !== false)
      .map(link => ({ ...link, label: pageConfig(link)?.title ?? link.label })),
  }))
  .filter(group => group.links.length > 0));

const year = computed<number>(() => DateTime.now().year);

const producer = computed<WhitelabelProducer>(() => producersStore.selected);

const footerLines = computed<string[]>(() => [ producer.value.footer ].flat());
</script>

<style scoped>
strong {
  font-size: 1.5rem;
  font-weight: 400;
}
.footer__container {
  text-align: left;
  font-weight: 300;
  font-size: 0.9rem;
}
.whatsapp__button {
  background-color: #25d366;
}
.whatsapp__button sub {
  font-size: 1.2rem;
  position: relative;
  top: 2px;
}

.footer-title-text {
  font-size: 16px;
}

.footer-item-text {
  font-size: 14px;
}

.copyright-row {
  background: black;
}

.v-footer {
  padding: 0;
}
</style>
